// react
import React from 'react';
import styled from 'styled-components';

// components
import { Button } from '@/components/atoms';
import { RowAction } from '@/index';

export interface SimpleTableRow {
  label: string;
  value: string;
}

export interface SimpleTableProps {
  rows: SimpleTableRow[];
  actions?: RowAction[];
}

/**
 * SimpleTable displays a list of question-answer pairs in a horizontal table layout.
 */
export const SimpleTable = ({ rows, actions }: SimpleTableProps) => {
  return (
    <>
      {' '}
      <TableContainer>
        <Table>
          <tbody>
            {rows.map((row: SimpleTableRow, index: number) => (
              <tr key={index}>
                <LabelCell>{row.label}</LabelCell>
                <ValueCell>{row.value}</ValueCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
      <ButtonContainer>
        {actions?.map((action, index) => (
          <Button
            key={index}
            ariaLabel={action.label}
            variant={action.buttonVariant || 'primary'}
            onPress={() => action.onPress(rows)}
            iconLeft={action.iconLeft}
            size={action.buttonSize || 'md'}
            isDisabled={action.isDisabled}
          >
            {action.label}
          </Button>
        ))}
      </ButtonContainer>
    </>
  );
};

const TableContainer = styled.div`
  width: 100%;
  overflow-y: auto;
  max-height: 600px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: ${(props) => props.theme.spacing[16]};
  table-layout: fixed;
  th {
    text-align: left;
    padding: ${(props) => props.theme.spacing[12]};
    font: ${(props) => props.theme.typography.body.md.medium};
    color: ${(props) => props.theme.color.neutral[90]};
    border-bottom: 1px solid ${(props) => props.theme.color.neutral[30]};
    width: 50%;
  }
`;

const LabelCell = styled.td`
  padding: ${(props) => props.theme.spacing[12]};
  font: ${(props) => props.theme.typography.body.md.medium};
  color: ${(props) => props.theme.color.neutral[90]};
  border-bottom: 1px solid ${(props) => props.theme.color.neutral[30]};
  vertical-align: middle;
  width: 50%;
  word-break: break-word;
  font-weight: bold;
`;

const ValueCell = styled.td`
  padding: ${(props) => props.theme.spacing[12]};
  font: ${(props) => props.theme.typography.body.md.regular};
  color: ${(props) => props.theme.color.neutral[70]};
  border-bottom: 1px solid ${(props) => props.theme.color.neutral[30]};
  vertical-align: middle;
  white-space: pre-wrap;
  width: 50%;
  word-break: break-word;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${(props) => props.theme.spacing[16]};
`;

SimpleTable.displayName = 'SimpleTable';

export default SimpleTable;
